export const CloverRefundOutcome = {
    REFUNDED: 'refunded',
    REJECTED: 'rejected',
    MALFORMED_RESPONSE: 'malformed_response',
} as const;

export type CloverRefundOutcome = (typeof CloverRefundOutcome)[keyof typeof CloverRefundOutcome];

export const CloverRefundPaymentResult = {
    SUCCESS: 'SUCCESS',
    FAIL: 'FAIL',
    INITIATED: 'INITIATED',
    VOIDED: 'VOIDED',
    VOIDING: 'VOIDING',
    VOID_FAILED: 'VOID_FAILED',
    AUTH: 'AUTH',
    AUTH_COMPLETED: 'AUTH_COMPLETED',
    DISCOUNT: 'DISCOUNT',
} as const;

export type CloverRefundPaymentResult =
    (typeof CloverRefundPaymentResult)[keyof typeof CloverRefundPaymentResult];

export const CloverRefundKind = {
    CLOUD_PAY: 'CLOUD_PAY',
    ECOMMERCE: 'ECOMMERCE',
} as const;

export type CloverRefundKind = (typeof CloverRefundKind)[keyof typeof CloverRefundKind];

export interface CloverCloudPayRefundRequestHeaders {
    readonly deviceId: string;
    readonly idempotencyKey: string;
    readonly posId: string;
    readonly userAgent: string;
    readonly timeoutSeconds?: number;
}

export interface CloverCloudPayRefundRequestBody {
    readonly amount?: number;
    readonly fullRefund?: boolean;
}

export interface CloverCloudPayRefundRequestDetails {
    readonly kind: typeof CloverRefundKind.CLOUD_PAY;
    readonly method: 'POST';
    readonly path: '/v1/payments/{paymentId}/refunds';
    readonly paymentId: string;
    readonly headers: CloverCloudPayRefundRequestHeaders;
    readonly body: CloverCloudPayRefundRequestBody;
}

export interface CloverEcommerceRefundRequestDetails {
    readonly kind: typeof CloverRefundKind.ECOMMERCE;
    readonly method: 'POST';
    readonly path: '/v1/refunds';
    readonly chargeId: string;
    readonly headers: {
        readonly idempotencyKey: string;
        readonly userAgent: string;
    };
    readonly body: {
        readonly charge: string;
        readonly amount?: number;
    };
}

export type CloverRefundRequestDetails =
    | CloverCloudPayRefundRequestDetails
    | CloverEcommerceRefundRequestDetails;

export interface CloverRefundReferenceBody {
    readonly id?: string;
}

export interface CloverRefundAdditionalChargeBody {
    readonly id?: string;
    readonly amount: number;
    readonly rate?: number;
    readonly type: 'CREDIT_SURCHARGE';
    readonly refund?: {
        readonly id: string;
    };
}

export interface CloverRefundAdditionalChargesBody {
    readonly elements: readonly CloverRefundAdditionalChargeBody[];
}

export interface CloverRefundAmounts {
    readonly underlyingAmountMinorUnits: number;
    readonly creditSurchargeAmountMinorUnits: number;
    readonly totalAmountMinorUnits: number;
}

export interface CloverRefundCardTransactionBody {
    readonly authCode?: string;
    readonly referenceId?: string;
    readonly state?: string;
    readonly token?: string;
    readonly transactionNo?: string;
    readonly type?: string;
}

export interface CloverRefundPaymentBody {
    readonly id?: string;
    readonly amount?: number;
    readonly result?: CloverRefundPaymentResult;
    readonly cardTransaction?: CloverRefundCardTransactionBody;
    readonly externalPaymentId?: string;
    readonly externalReferenceId?: string;
    readonly refunds?: readonly CloverRefundBody[];
}

export interface CloverRefundBody {
    readonly id?: string;
    readonly amount?: number;
    readonly cardTransaction?: CloverRefundCardTransactionBody;
    readonly createdTime?: number;
    readonly externalReferenceId?: string;
    readonly payment?: CloverRefundReferenceBody;
    readonly taxAmount?: number;
    readonly tipAmount?: number;
    readonly voided?: boolean;
    readonly additionalCharges?: CloverRefundAdditionalChargesBody;
}

export interface CloverRefundResponseBody {
    readonly payment?: CloverRefundPaymentBody;
    readonly paymentId: string;
    readonly refund?: CloverRefundBody;
}

export interface CloverRefundErrorBody {
    readonly message: string;
    readonly type: string;
    readonly code?: string;
    readonly requestId?: string;
    readonly requestType?: string;
}

export interface CloverCloudPayRefundSuccessfulResponseDetails {
    readonly kind: typeof CloverRefundKind.CLOUD_PAY;
    readonly outcome: typeof CloverRefundOutcome.REFUNDED;
    readonly httpStatus: 200;
    readonly body: CloverRefundResponseBody;
    readonly refundAmounts?: CloverRefundAmounts;
}

export interface CloverEcommerceRefundResponseBody {
    readonly id: string;
    readonly object: 'refund';
    readonly amount: number;
    readonly charge: string;
    readonly created: number;
    readonly status: 'succeeded';
    readonly additional_charges?: CloverRefundAdditionalChargeBody;
    readonly metadata?: {
        readonly refundType?: string;
        readonly authCode?: string;
        readonly refNum?: string;
        readonly gatewayResponseCode?: string;
    };
}

export interface CloverEcommerceRefundSuccessfulResponseDetails {
    readonly kind: typeof CloverRefundKind.ECOMMERCE;
    readonly outcome: typeof CloverRefundOutcome.REFUNDED;
    readonly httpStatus: 200;
    readonly body: CloverEcommerceRefundResponseBody;
    readonly refundAmounts?: CloverRefundAmounts;
}

export type CloverRefundSuccessfulResponseDetails =
    | CloverCloudPayRefundSuccessfulResponseDetails
    | CloverEcommerceRefundSuccessfulResponseDetails;

export interface CloverRefundRejectedResponseDetails {
    readonly kind: CloverRefundKind;
    readonly outcome: typeof CloverRefundOutcome.REJECTED;
    readonly httpStatus: number;
    readonly body: CloverRefundErrorBody;
}

export interface CloverRefundMalformedResponseDetails {
    readonly kind: CloverRefundKind;
    readonly outcome: typeof CloverRefundOutcome.MALFORMED_RESPONSE;
    readonly httpStatus: number;
    readonly body: unknown;
    readonly message: string;
}

export type CloverRefundResponseDetails =
    | CloverRefundSuccessfulResponseDetails
    | CloverRefundRejectedResponseDetails
    | CloverRefundMalformedResponseDetails;
